import {
  ActivityNames,
  CaseReport,
  CaseReportSegment,
} from './CaseTypes';
import { formatTimeDisplay, getActivityColor, getActivityLabel } from '../utils/caseUtils';

/**
 * Video playback information for a segment.
 */
export interface SegmentVideoInfo {
  signedUrl: string;
  videoStartSec: number;
  videoEndSec: number;
  videoIsValid: boolean;
}

/**
 * Represents a single segment of a stacked bar in the cannulation chart.
 */
export interface CannulationBarSegment {
  activityType: string;      // ActivityNames enum value
  duration: number;          // Duration in minutes
  displayDuration: string;   // Formatted time string
  color: string;             // CSS variable from caseUtils
  label: string;             // Display label for the activity
  // Video playback info (earliest start to latest end for this activity at this locus)
  videoInfo?: SegmentVideoInfo;
}

/**
 * Represents all data for a single locus bar (stacked segments).
 */
export interface CannulationBarData {
  locus: string;             // e.g., "T2-L", "T2-R", "T3-L"
  segments: CannulationBarSegment[];
  totalTime: number;         // Total time for this locus in minutes
  displayTotalTime: string;  // Formatted total time string

  // Individual segment durations for recharts (stacked bar requires flat properties)
  // Allow indexing by any string to support ActivityNames values
  [key: string]: string | CannulationBarSegment[] | number | undefined;
}

/**
 * Activity types relevant for the cannulation chart.
 */
const CANNULATION_ACTIVITY_TYPES = [
  ActivityNames.ANATOMY_LOCALIZATION,
  ActivityNames.CANNULATION,
  ActivityNames.SCREW_PLACEMENT,
  ActivityNames.CANNULATION_SCREW_PLACEMENT,
];

/**
 * Order for displaying split cannulation activities (bottom to top in stacked bar).
 */
const SPLIT_ACTIVITY_ORDER = [
  ActivityNames.ANATOMY_LOCALIZATION,
  ActivityNames.CANNULATION,
  ActivityNames.SCREW_PLACEMENT,
];

/**
 * Parses a vertebra locus string and returns sort keys.
 * Handles formats like "T2-L", "T2-R", "L1-L", "L1-R", "S1-L", etc.
 *
 * Sort order:
 * 1. Region: C (cervical) < T (thoracic) < L (lumbar) < S (sacral)
 * 2. Level number: ascending (e.g., T2 < T3 < T10)
 * 3. Side: L (left) < R (right)
 */
function parseLocusForSort(locus: string): { regionOrder: number; level: number; sideOrder: number } {
  const regionOrder: Record<string, number> = { C: 0, T: 1, L: 2, S: 3 };
  const sideOrder: Record<string, number> = { L: 0, R: 1 };

  // Match pattern: Region letter(s), level number, optional side indicator
  const match = locus.match(/^([CTLS])(\d+)(?:-([LR]))?$/i);

  if (!match) {
    // Fallback for unexpected formats
    return { regionOrder: 99, level: 99, sideOrder: 99 };
  }

  const region = match[1].toUpperCase();
  const level = parseInt(match[2], 10);
  const side = match[3]?.toUpperCase() || 'L'; // Default to L if no side specified

  return {
    regionOrder: regionOrder[region] ?? 99,
    level,
    sideOrder: sideOrder[side] ?? 99,
  };
}

/**
 * Sorts loci in anatomical order (superior to inferior, left to right).
 */
function sortLoci(loci: string[]): string[] {
  return [...loci].sort((a, b) => {
    const aSort = parseLocusForSort(a);
    const bSort = parseLocusForSort(b);

    // First by region
    if (aSort.regionOrder !== bSort.regionOrder) {
      return aSort.regionOrder - bSort.regionOrder;
    }
    // Then by level number
    if (aSort.level !== bSort.level) {
      return aSort.level - bSort.level;
    }
    // Finally by side (L before R)
    return aSort.sideOrder - bSort.sideOrder;
  });
}

/**
 * Adapts CaseReport data into chart-ready format for the CannulationScrewPlacementChart.
 * Handles both combined (v1.0) and split (v1.2) cannulation data formats.
 */
export default class CannulationScrewPlacementAdapter {
  private caseReport: CaseReport;
  private cachedData: CannulationBarData[] | null = null;

  constructor(caseReport: CaseReport) {
    this.caseReport = caseReport;
  }

  /**
   * Detects if the case report uses split cannulation data (v1.2) or combined (v1.0).
   * Checks both the totals flag and actual segment data.
   */
  isSplitData(): boolean {
    // Check the totals flag
    if (this.caseReport.totals?.has_split_cannulation_data) {
      return true;
    }

    // Also check segment data for split activity types
    if (!this.caseReport.segments) {
      return false;
    }

    return this.caseReport.segments.some(
      (segment) =>
        segment.segmentType === ActivityNames.ANATOMY_LOCALIZATION ||
        segment.segmentType === ActivityNames.CANNULATION ||
        segment.segmentType === ActivityNames.SCREW_PLACEMENT
    );
  }

  /**
   * Returns the activity types present in the data (for legend rendering).
   * Order is maintained for consistent legend display.
   */
  getActivityTypes(): string[] {
    if (this.isSplitData()) {
      // Return split activities in display order
      return SPLIT_ACTIVITY_ORDER.filter((activity) =>
        this.getCannulationData().some((bar) =>
          bar.segments.some((seg) => seg.activityType === activity)
        )
      );
    } else {
      // Combined data - only CANNULATION_SCREW_PLACEMENT
      return [ActivityNames.CANNULATION_SCREW_PLACEMENT];
    }
  }

  /**
   * Returns cannulation data grouped by locus, sorted in anatomical order.
   * Each locus contains stacked segments for each activity type present.
   */
  getCannulationData(): CannulationBarData[] {
    // Return cached data if available
    if (this.cachedData) {
      return this.cachedData;
    }

    if (!this.caseReport.segments || this.caseReport.segments.length === 0) {
      this.cachedData = [];
      return this.cachedData;
    }

    // Filter to only cannulation-related segments
    const cannulationSegments = this.caseReport.segments.filter((segment) =>
      CANNULATION_ACTIVITY_TYPES.includes(segment.segmentType as ActivityNames)
    );

    if (cannulationSegments.length === 0) {
      this.cachedData = [];
      return this.cachedData;
    }

    // Group segments by locus and activity type
    // Track both duration and raw segments for video timing
    interface SegmentAccumulator {
      duration: number;
      rawSegments: CaseReportSegment[];
    }
    const locusByType = new Map<string, Map<string, SegmentAccumulator>>();

    cannulationSegments.forEach((segment: CaseReportSegment) => {
      const locus = segment.segmentValue || 'Unknown';
      const duration = segment.normalizedEndMin - segment.normalizedStartMin;

      // Get or create the type map for this locus
      let typeMap = locusByType.get(locus);
      if (!typeMap) {
        typeMap = new Map();
        locusByType.set(locus, typeMap);
      }

      const existing = typeMap.get(segment.segmentType);
      if (existing) {
        existing.duration += duration;
        existing.rawSegments.push(segment);
      } else {
        typeMap.set(segment.segmentType, {
          duration,
          rawSegments: [segment],
        });
      }
    });

    // Sort loci in anatomical order
    const sortedLoci = sortLoci(Array.from(locusByType.keys()));

    // Build CannulationBarData for each locus
    const isSplit = this.isSplitData();
    const activityOrder = isSplit ? SPLIT_ACTIVITY_ORDER : [ActivityNames.CANNULATION_SCREW_PLACEMENT];

    this.cachedData = sortedLoci.map((locus) => {
      const typeMap = locusByType.get(locus) ?? new Map<string, SegmentAccumulator>();
      const segments: CannulationBarSegment[] = [];
      let totalTime = 0;

      // Build bar data with flat properties for recharts
      const barData: CannulationBarData = {
        locus,
        segments: [],
        totalTime: 0,
        displayTotalTime: '0m',
      };

      activityOrder.forEach((activity) => {
        const accumulator = typeMap.get(activity);
        if (accumulator && accumulator.duration > 0) {
          // Calculate video timing from raw segments (earliest start, latest end)
          const rawSegs = accumulator.rawSegments;
          const validSegs = rawSegs.filter(s => s.videoIsValid && s.signedUrl);

          let videoInfo: SegmentVideoInfo | undefined;
          if (validSegs.length > 0) {
            // All segments should share the same signedUrl - if not, use first segment only
            const firstSeg = validSegs[0];
            const allSameUrl = validSegs.every((s) => s.signedUrl === firstSeg.signedUrl);

            if (allSameUrl) {
              // Safe to compute span across segments
              const earliestStart = Math.min(...validSegs.map((s) => s.videoStartSec ?? 0));
              const latestEnd = Math.max(...validSegs.map((s) => s.videoEndSec ?? 0));
              videoInfo = {
                signedUrl: firstSeg.signedUrl,
                videoStartSec: earliestStart,
                videoEndSec: latestEnd,
                videoIsValid: true,
              };
            } else {
              // Unexpected: fall back to first segment only
              videoInfo = {
                signedUrl: firstSeg.signedUrl,
                videoStartSec: firstSeg.videoStartSec ?? 0,
                videoEndSec: firstSeg.videoEndSec ?? 0,
                videoIsValid: true,
              };
            }
          }

          segments.push({
            activityType: activity,
            duration: accumulator.duration,
            displayDuration: formatTimeDisplay(accumulator.duration, true),
            color: getActivityColor(activity),
            label: getActivityLabel(activity),
            videoInfo,
          });
          totalTime += accumulator.duration;

          // Add flat property for recharts stacked bar
          barData[activity] = accumulator.duration;
        }
      });

      barData.segments = segments;
      barData.totalTime = totalTime;
      barData.displayTotalTime = formatTimeDisplay(totalTime, true);

      return barData;
    });

    return this.cachedData;
  }

  /**
   * Calculates the average time per locus and formats it for display.
   */
  getAverageTimePerLevel(): string {
    const data = this.getCannulationData();
    if (data.length === 0) {
      return '0m';
    }

    const totalMinutes = data.reduce((sum, bar) => sum + bar.totalTime, 0);
    const avgMinutes = totalMinutes / data.length;

    return formatTimeDisplay(avgMinutes, true);
  }

  /**
   * Returns the total cannulation time across all loci.
   */
  getTotalTime(): number {
    const data = this.getCannulationData();
    return data.reduce((sum, bar) => sum + bar.totalTime, 0);
  }

  /**
   * Clears the cached data, forcing recalculation on next access.
   */
  clearCache(): void {
    this.cachedData = null;
  }
}
